import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import FormRequiredMark from 'components/form/form-required-mark/FormRequiredMark';

import CloudinaryImage from 'components/CloudinaryImage';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import ImageCropper from 'components/image-cropper/ImageCropper';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { __t, applyTranslations } from 'utils/language';
import { getCloudinaryImageUrl } from 'utils/cloudinary';
import { getPreparedPreloadImage } from 'utils/image';
import { expandDataProps } from 'utils/helper/common';

import { REALM_PROFILE_APP_COVER_DIMENSIONS, REALM_PROFILE_DESKTOP_COVER_DIMENSIONS } from 'modules/realm-outfits/constants';

import type { Data, Image, ImageProps, ImageSize, ImageType } from 'types';
import CrossButton from 'components/buttons/cross-button/CrossButton';

applyTranslations(require('./cropped-profile-image.i18n.json'));

const EDIT_ICON = 'pencil-inside-circle-custom-fg';
const ADD_ICON = 'plus-inside-circle-custom-fg';

const COVER_OPTIONS = {
  width: 960,
  height: 420,
  border: 0,
  borderRadius: 0,
  repositionMode: 'inline',
  editIcon: EDIT_ICON,
  addIcon: ADD_ICON
};

const COVER_OPTIONS_DESKTOP = {
  width: 400,
  height: (REALM_PROFILE_DESKTOP_COVER_DIMENSIONS.height / REALM_PROFILE_DESKTOP_COVER_DIMENSIONS.width) * 400,
  border: 0,
  borderRadius: 1,
  repositionMode: 'modal',
  editIcon: EDIT_ICON,
  addIcon: ADD_ICON
};

const COVER_OPTIONS_APP = {
  width: 144,
  height: (REALM_PROFILE_APP_COVER_DIMENSIONS.height / REALM_PROFILE_APP_COVER_DIMENSIONS.width) * 144,
  border: 0,
  borderRadius: 1,
  repositionMode: 'modal',
  editIcon: EDIT_ICON,
  addIcon: ADD_ICON
};

const EMS_PROFILE_OPTIONS = {
  width: 144,
  height: 144,
  border: 0,
  borderRadius: 1,
  repositionMode: 'modal',
  editIcon: EDIT_ICON,
  addIcon: ADD_ICON
};

const PROFILE_OPTIONS = {
  width: 144,
  height: 144,
  border: 0,
  borderRadius: 72,
  repositionMode: 'modal',
  editIcon: EDIT_ICON,
  addIcon: ADD_ICON
};

const AGENCY_COVER_OPTIONS = {
  width: 1200,
  height: 394,
  border: 0,
  borderRadius: 0,
  repositionMode: 'inline',
  editIcon: EDIT_ICON,
  addIcon: ADD_ICON
};

const IMAGE_OPTIONS = {
  'cover': COVER_OPTIONS,
  'profile': PROFILE_OPTIONS,
  'ems-profile': EMS_PROFILE_OPTIONS,
  'agency': AGENCY_COVER_OPTIONS,
  'cover-medium': {
    ...COVER_OPTIONS,
    width: 934,
    height: 365
  },
  'cover-small': {
    ...COVER_OPTIONS,
    width: 449,
    height: 112
  },
  'cover-desktop': COVER_OPTIONS_DESKTOP,
  'cover-app': COVER_OPTIONS_APP,
  'profile-small': {
    ...PROFILE_OPTIONS,
    width: 64,
    height: 64
  }
};

type OwnProps = {
  type: ImageType,
  canEdit: boolean,
  image: Image | undefined | null,
  onChange?: (a: Image) => Promise<Image | undefined>,
  onDelete?: () => void,
  data?: Data,
  fallback?: string,
  customSize?: {
    width: number,
    height: number
  }
};

type StateProps = {
  dispatch: any
};

type Props = OwnProps & StateProps;

type State = {
  isEditMode: boolean
};

class CroppedProfileImage extends React.PureComponent<Props, State> {
  state = {
    isEditMode: false
  };

  startEditing = (): void => {
    this.setState({
      isEditMode: true
    });
  };

  finishEditing = (): void => {
    this.setState({
      isEditMode: false
    });
  };

  handleChange = (updatedImage: Image, size: ImageSize): void => {
    const { onChange, dispatch } = this.props;

    if (!onChange) {
      return;
    }

    const loadingId = 'CroppedProfileImage.handleChange';
    dispatch(showLoading(loadingId));

    onChange(updatedImage)
      // $FlowIgnore - Flow doesn't like `File` type but we don't want to create a function just for this instance
      .then((createdImage) => getPreparedPreloadImage(updatedImage, getCloudinaryImageUrl(createdImage, size)))
      .then(() => this.finishEditing())
      .then(() => dispatch(hideLoading(loadingId)))
      .catch(() => dispatch(hideLoading(loadingId)));
  };

  handleDelete = (event): void => {
    const { onDelete } = this.props;

    event.stopPropagation();

    if (onDelete) {
      onDelete();
    }
  };

  renderImage(
    image: Image | undefined | null,
    type: ImageType,
    canEdit: boolean,
    fallback?: string | null
  ): React.ReactNode {
    const { customSize } = this.props;

    const pictureClasses = classnames({
      'cropped-profile-image__picture': true,
      'cropped-profile-image__picture--empty': !canEdit && !image,
      [`cropped-profile-image__picture--type-${type}`]: type,
      [`cropped-profile-image__picture--type-${type}-empty`]: !image
    });

    const imageProps: ImageProps = {
      image,
      width: customSize?.width ?? IMAGE_OPTIONS[type].width,
      height: customSize?.height ?? IMAGE_OPTIONS[type].height
    };

    if (type === 'cover') {
      imageProps.resizable = true;
    }

    return (
      <div className={pictureClasses}>
        {/* $FlowIgnore - Cannot create CloudinaryImage element b/c many props are incompatible */}
        <CloudinaryImage
          {...imageProps}
        />
        {
          isEmpty(image) && fallback && (
            <span className="cropped-profile-image__fallback">
              {fallback}
            </span>
          )
        }
      </div>
    );
  }

  renderOverlay(image: Image | undefined | null, type: ImageType): React.ReactNode {
    const { onDelete } = this.props;
    const hasImage = Boolean(image);

    const label = __t(`cropped_profile_image.label_${hasImage ? 'edit' : 'upload'}_${type}_image`);
    const dimensions = __t(`cropped_profile_image.dimensions_${type}_image`);

    const overlayClasses = classnames({
      'cropped-profile-image__overlay': true,
      'cropped-profile-image__overlay--light': !hasImage
    });

    const iconClasses = classnames({
      'cropped-profile-image__overlay-icon': true,
      [`cropped-profile-image__overlay-icon--type-${type}`]: type
    });

    const labelClasses = classnames({
      'cropped-profile-image__overlay-label': true,
      [`cropped-profile-image__overlay-label--type-${type}`]: type
    });

    return (
      <div className={overlayClasses}>
        <div className={iconClasses}>
          <SVGDynamicIcon
            name={hasImage ? EDIT_ICON : ADD_ICON}
            width="100%"
            height="100%"
            customColor="white"
          />
        </div>
        {
          hasImage && onDelete && (
            <CrossButton
              onClick={this.handleDelete}
              type="corner-closer"
            />
          )
        }
        {
          label && (
            <div className={labelClasses}>
              {label}
              {
                !hasImage &&
                  <FormRequiredMark />
              }
            </div>
          )
        }
        {
          dimensions && (
            <div>
              {dimensions}
            </div>
          )
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const { image, fallback, customSize } = this.props;

    const { canEdit, type, data } = this.props;
    const { isEditMode } = this.state;
    const imageOptions = IMAGE_OPTIONS[type];
    const isWithModal = imageOptions.repositionMode === 'modal';

    const classes = classnames({
      'cropped-profile-image': true,
      'cropped-profile-image--editable': !isEditMode && canEdit,
      'cropped-profile-image--with-modal': isEditMode && isWithModal,
      [`cropped-profile-image--type-${type}`]: type
    });

    if (!canEdit) {
      return (
        <section
          className={classes}
          {...expandDataProps(data)}
        >
          {this.renderImage(image, type, canEdit, fallback)}
        </section>
      );
    }

    if (!isEditMode) {
      return (
        <section
          className={classes}
          style={{
            width: customSize?.width ?? imageOptions.width,
            height: customSize?.height ?? imageOptions.height
          }}
          onClick={this.startEditing}
        >
          {this.renderImage(image, type, canEdit)}
          {this.renderOverlay(image, type)}
        </section>
      );
    }

    return (
      <section className={classes}>
        <ImageCropper
          image={image}
          width={customSize?.width ?? imageOptions.width}
          height={customSize?.height ?? imageOptions.height}
          border={imageOptions.border}
          borderRadius={imageOptions.borderRadius}
          mode={imageOptions.repositionMode}
          onChange={this.handleChange}
          onCancel={this.finishEditing}
        />
        {
          isWithModal &&
            this.renderImage(image, type, canEdit)
        }
      </section>
    );
  }
}

export default connect()(CroppedProfileImage);
